/**
 * campaign.js — interactivity for the blog + campaign landing slices
 * (templates/blog.json, templates/article.json, page.get-matcha.json,
 * page.matcha-get.json).
 *
 * Ports (from the headless app):
 *  - features/landing/get-matcha/research-tabs.tsx     -> [data-tabs]
 *  - components/ui/accordion.tsx (shadcn/Radix)        -> [data-accordion]
 *  - features/landing/get-matcha/quality-testing.tsx   -> [data-qt-accordion]
 *  - features/landing/get-matcha/sticky-cta.tsx        -> [data-sticky-cta]
 *  - scrollIntoView CTAs (hero/ritual/final-cta)       -> [data-scroll-to]
 *  - features/landing/get-matcha/offer-section.tsx     -> [data-offer]
 *    (tier selection + progressive gift tiers from the gifts App Proxy,
 *     same response shape as lib/gifts.ts / assets/cart.js)
 *
 * Markup contracts (all Tailwind classes live in the .liquid sections; this
 * file only swaps class lists carried in data-class-* attributes, so the CSS
 * build never needs to scan this file):
 *
 *  Tabs:
 *    <div data-tabs="<default-value>">
 *      <button data-tab-trigger="v">…</button>   (data-state toggled)
 *      <div data-tab-panel="v">…</div>           (hidden + data-state toggled)
 *
 *  Accordion (single, collapsible — shadcn semantics):
 *    <div data-accordion>
 *      <div data-accordion-item data-default-open="true|false">
 *        <button data-accordion-trigger>… <svg/></button>
 *        <div data-accordion-panel>…</div>
 *
 *  Quality-testing accordion (max-height transition variant):
 *    <div data-qt-accordion>
 *      <div data-qt-item>
 *        <button data-qt-trigger>… <svg><path data-qt-vline/></svg></button>
 *        <div data-qt-panel data-class-open="…" data-class-closed="…">…</div>
 *
 *  Sticky CTA:
 *    <div data-sticky-cta data-sticky-watch="offer"
 *         data-class-visible="…" data-class-hidden="…">
 *    Hero reference element: [data-campaign-hero] (falls back to first
 *    <section> like the source component).
 *
 *  Scroll buttons: <button data-scroll-to="offer">
 *
 *  Offer:
 *    <div data-offer data-product-id="123" data-default-index="1">
 *      <div data-offer-tier data-index="0" data-class-active="…" data-class-inactive="…">
 *        … inner [data-class-active]/[data-class-inactive] nodes,
 *          [data-offer-radio-dot] …
 *      <div data-offer-panel data-index="0" data-quantity="1">
 *        [data-offer-gifts] (hidden), [data-offer-gift-rows],
 *        [data-offer-gift-value] (hidden), [data-offer-teaser] (hidden),
 *        [data-offer-shipping-plain]/[data-offer-shipping-gifts]
 *      <template data-offer-gift-template> gift row markup </template>
 *      <form …><input name="quantity" data-offer-qty></form>
 */
(function () {
  "use strict";

  if (window.__campaignJs) return;
  window.__campaignJs = true;

  function classList(attr) {
    return (attr || "").split(/\s+/).filter(Boolean);
  }

  /** Swap data-class-active/-inactive (or any pair of attrs) on an element. */
  function applyState(el, on, activeAttr, inactiveAttr) {
    const active = classList(el.getAttribute(activeAttr || "data-class-active"));
    const inactive = classList(
      el.getAttribute(inactiveAttr || "data-class-inactive"),
    );
    el.classList.remove(...active, ...inactive);
    el.classList.add(...(on ? active : inactive));
  }

  /* ------------------------------------------------------------------ */
  /* Tabs (port of research-tabs.tsx / shadcn Tabs)                      */
  /* ------------------------------------------------------------------ */
  function initTabs(root) {
    if (root.__inited) return;
    root.__inited = true;
    const triggers = Array.from(root.querySelectorAll("[data-tab-trigger]"));
    const panels = Array.from(root.querySelectorAll("[data-tab-panel]"));

    function select(value) {
      triggers.forEach((t) => {
        const on = t.getAttribute("data-tab-trigger") === value;
        t.setAttribute("data-state", on ? "active" : "inactive");
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.setAttribute("tabindex", on ? "0" : "-1");
      });
      panels.forEach((p) => {
        const on = p.getAttribute("data-tab-panel") === value;
        p.toggleAttribute("hidden", !on);
        p.setAttribute("data-state", on ? "active" : "inactive");
      });
    }

    triggers.forEach((t) => {
      t.addEventListener("click", () =>
        select(t.getAttribute("data-tab-trigger")),
      );
    });

    const initial =
      root.getAttribute("data-tabs") ||
      (triggers[0] && triggers[0].getAttribute("data-tab-trigger"));
    if (initial) select(initial);
  }

  /* ------------------------------------------------------------------ */
  /* Accordion — single open, collapsible (shadcn/Radix semantics).      */
  /* Chevron rotation comes from the trigger's                           */
  /* [&[data-state=open]>svg]:rotate-180 utility in the markup.          */
  /* ------------------------------------------------------------------ */
  function initAccordion(root) {
    if (root.__inited) return;
    root.__inited = true;
    const items = Array.from(root.querySelectorAll("[data-accordion-item]"));

    function setOpen(item, open) {
      const state = open ? "open" : "closed";
      item.setAttribute("data-state", state);
      const trigger = item.querySelector("[data-accordion-trigger]");
      const panel = item.querySelector("[data-accordion-panel]");
      if (trigger) {
        trigger.setAttribute("data-state", state);
        trigger.setAttribute("aria-expanded", open ? "true" : "false");
      }
      if (panel) {
        panel.toggleAttribute("hidden", !open);
        panel.setAttribute("data-state", state);
      }
    }

    items.forEach((item) => {
      const trigger = item.querySelector("[data-accordion-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        const wasOpen = item.getAttribute("data-state") === "open";
        items.forEach((i) => setOpen(i, false));
        if (!wasOpen) setOpen(item, true);
      });
    });

    items.forEach((item) =>
      setOpen(item, item.getAttribute("data-default-open") === "true"),
    );
  }

  /* ------------------------------------------------------------------ */
  /* Quality-testing accordion (max-h / opacity transition variant)      */
  /* ------------------------------------------------------------------ */
  function initQtAccordion(root) {
    if (root.__inited) return;
    root.__inited = true;
    const items = Array.from(root.querySelectorAll("[data-qt-item]"));

    function setOpen(item, open) {
      item.setAttribute("data-state", open ? "open" : "closed");
      const trigger = item.querySelector("[data-qt-trigger]");
      const panel = item.querySelector("[data-qt-panel]");
      const vline = item.querySelector("[data-qt-vline]");
      if (trigger) trigger.setAttribute("aria-expanded", open ? "true" : "false");
      if (panel) applyState(panel, open, "data-class-open", "data-class-closed");
      if (vline) vline.classList.toggle("hidden", open);
    }

    items.forEach((item) => {
      const trigger = item.querySelector("[data-qt-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        const wasOpen = item.getAttribute("data-state") === "open";
        items.forEach((i) => setOpen(i, false));
        setOpen(item, !wasOpen);
      });
    });

    items.forEach((item) => setOpen(item, false));
  }

  /* ------------------------------------------------------------------ */
  /* Sticky CTA (port of sticky-cta.tsx): visible once the hero has      */
  /* scrolled past, hidden again while the offer section is on screen.   */
  /* ------------------------------------------------------------------ */
  function initStickyCta(bar) {
    if (bar.__inited) return;
    bar.__inited = true;
    let visible = null;

    function setVisible(next) {
      if (next === visible) return;
      visible = next;
      applyState(bar, next, "data-class-visible", "data-class-hidden");
      // Parity with the app: floating widgets listen for this event.
      window.dispatchEvent(
        new CustomEvent("sticky-cart-visibility", { detail: { visible: next } }),
      );
    }

    function onScroll() {
      const hero =
        document.querySelector("[data-campaign-hero]") ||
        document.querySelector("section");
      const heroBottom = hero ? hero.getBoundingClientRect().bottom : 0;
      const watchId = bar.getAttribute("data-sticky-watch") || "offer";
      const watched = document.getElementById(watchId);
      const rect = watched && watched.getBoundingClientRect();
      const watchedVisible =
        rect && rect.top < window.innerHeight && rect.bottom > 0;
      setVisible(heroBottom < 0 && !watchedVisible);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
  }

  /* ------------------------------------------------------------------ */
  /* Smooth-scroll CTAs                                                  */
  /* ------------------------------------------------------------------ */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-scroll-to]");
    if (!btn) return;
    const target = document.getElementById(btn.getAttribute("data-scroll-to"));
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: "smooth" });
    }
  });

  /* ------------------------------------------------------------------ */
  /* Offer section (port of offer-section.tsx)                           */
  /* ------------------------------------------------------------------ */
  function initOffer(root) {
    if (root.__inited) return;
    root.__inited = true;
    const tiers = Array.from(root.querySelectorAll("[data-offer-tier]"));
    const panels = Array.from(root.querySelectorAll("[data-offer-panel]"));
    const qtyInput = root.querySelector("[data-offer-qty]");

    function select(index) {
      tiers.forEach((tier) => {
        const on = Number(tier.getAttribute("data-index")) === index;
        tier.setAttribute("aria-pressed", on ? "true" : "false");
        if (tier.hasAttribute("data-class-active")) applyState(tier, on);
        tier.querySelectorAll("[data-class-active]").forEach((el) => {
          applyState(el, on);
        });
        const dot = tier.querySelector("[data-offer-radio-dot]");
        if (dot) dot.classList.toggle("hidden", !on);
      });
      let quantity = 1;
      panels.forEach((panel) => {
        const on = Number(panel.getAttribute("data-index")) === index;
        panel.toggleAttribute("hidden", !on);
        if (on) quantity = Number(panel.getAttribute("data-quantity")) || 1;
      });
      if (qtyInput) qtyInput.value = String(quantity);
    }

    tiers.forEach((tier) => {
      tier.addEventListener("click", () =>
        select(Number(tier.getAttribute("data-index")) || 0),
      );
    });

    select(Number(root.getAttribute("data-default-index")) || 0);
    loadOfferGifts(root, panels);
  }

  /* Same tier normalization as lib/gifts.ts / assets/cart.js. */
  function normalizeTierGifts(tier) {
    if (tier.gifts && tier.gifts.length > 0) return tier.gifts;
    if (tier.giftVariantId) {
      return [
        {
          variantId: tier.giftVariantId,
          title: tier.giftTitle,
          quantity: tier.giftQuantity,
          price: tier.giftPrice,
        },
      ];
    }
    return [];
  }

  async function loadOfferGifts(root, panels) {
    const config = window.themeConfig || {};
    const productId = root.getAttribute("data-product-id");
    if (!config.giftsEnabled || !config.giftsProxyPath || !productId) return;
    const gid = "gid://shopify/Product/" + productId;

    let rules = {};
    try {
      const res = await fetch(config.giftsProxyPath + "?productIds=" + gid);
      if (!res.ok) return;
      rules = (await res.json()).rules || {};
    } catch (e) {
      return; // gifts are a progressive enhancement — never block the offer
    }

    const rule = rules[gid];
    const hasTiers = rule && rule.enabled && rule.tiers && rule.tiers.length > 0;
    if (!hasTiers) return;

    const template = root.querySelector("template[data-offer-gift-template]");
    const sorted = [...rule.tiers].sort((a, b) => b.minQuantity - a.minQuantity);

    panels.forEach((panel) => {
      const quantity = Number(panel.getAttribute("data-quantity")) || 1;
      const tier = sorted.find(
        (t) =>
          quantity >= t.minQuantity &&
          (t.maxQuantity == null || quantity <= t.maxQuantity),
      );
      const gifts = tier ? normalizeTierGifts(tier) : [];
      const giftsBox = panel.querySelector("[data-offer-gifts]");
      const rowsBox = panel.querySelector("[data-offer-gift-rows]");
      const teaser = panel.querySelector("[data-offer-teaser]");
      const shippingPlain = panel.querySelector("[data-offer-shipping-plain]");
      const shippingGifts = panel.querySelector("[data-offer-shipping-gifts]");

      if (gifts.length > 0 && giftsBox && rowsBox && template) {
        rowsBox.innerHTML = "";
        let totalValue = 0;
        gifts.forEach((gift) => {
          const row = template.content.firstElementChild.cloneNode(true);
          const title = gift.title || "Free Gift";
          const titleEl = row.querySelector("[data-gift-title]");
          if (titleEl) titleEl.textContent = title;
          const priceNum = parseFloat(gift.price || "0") || 0;
          totalValue += priceNum;
          const strike = row.querySelector("[data-gift-price]");
          if (strike) {
            if (priceNum > 0 && window.themeUtils) {
              strike.textContent = window.themeUtils.formatMoney(
                Math.round(priceNum * 100),
              );
            } else {
              strike.remove();
            }
          }
          rowsBox.appendChild(row);
        });

        const valueEl = panel.querySelector("[data-offer-gift-value]");
        if (valueEl) {
          if (totalValue > 0 && window.themeUtils) {
            valueEl.textContent = (
              valueEl.getAttribute("data-t-gift-value") || "[[value]]"
            ).replace(
              "[[value]]",
              window.themeUtils.formatMoney(Math.round(totalValue * 100)),
            );
            valueEl.removeAttribute("hidden");
          }
        }

        giftsBox.removeAttribute("hidden");
        if (teaser) teaser.setAttribute("hidden", "");
        if (shippingPlain) shippingPlain.classList.add("hidden");
        if (shippingGifts) shippingGifts.classList.remove("hidden");
      } else if (teaser) {
        teaser.removeAttribute("hidden");
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */
  function scan(root) {
    const scope = root || document;
    scope.querySelectorAll("[data-tabs]").forEach(initTabs);
    scope.querySelectorAll("[data-accordion]").forEach(initAccordion);
    scope.querySelectorAll("[data-qt-accordion]").forEach(initQtAccordion);
    scope.querySelectorAll("[data-sticky-cta]").forEach(initStickyCta);
    scope.querySelectorAll("[data-offer]").forEach(initOffer);
  }

  if (window.themeUtils && window.themeUtils.onReady) {
    window.themeUtils.onReady(() => scan());
  } else if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(), { once: true });
  } else {
    scan();
  }
  document.addEventListener("campaign:scan", (e) => scan(e.target));
  window.themeCampaign = { scan: scan };
})();
